import axios from 'axios';

// Determine API base URL from environment variables
const getApiUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  
  if (!apiUrl) {
    console.warn('🚨 REACT_APP_API_URL not defined, using fallback URL');
    return process.env.NODE_ENV === 'production' 
      ? 'https://ai-panel.onrender.com' 
      : 'http://localhost:5000';
  }
  
  console.log('🌐 Using API URL:', apiUrl);
  return apiUrl;
};

// Helper to sanitize token
const sanitizeToken = (token) => {
  if (!token) return null;
  
  // Remove Bearer prefix if present
  let cleanToken = token.startsWith('Bearer ') ? token.substring(7).trim() : token.trim();
  
  // Check if token has valid JWT format (3 parts separated by dots)
  const parts = cleanToken.split('.');
  
  if (parts.length === 3) {
    return cleanToken; // Token is already valid
  }
  
  // Try to extract a valid JWT token
  const jwtPattern = /([A-Za-z0-9_-]+)\.([A-Za-z0-9_-]+)\.([A-Za-z0-9_-]+)/;
  const match = cleanToken.match(jwtPattern);
  
  if (match && match.length === 4) {
    console.log('🛠️ Fixed malformed token format');
    return `${match[1]}.${match[2]}.${match[3]}`;
  }
  
  console.error('❌ Unable to sanitize token');
  return null; // Token is invalid and can't be fixed
};

// Configure axios defaults
const baseURL = getApiUrl();

// Create axios instance with proper baseURL
const api = axios.create({
  baseURL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Check for token on initialization and apply it properly with Bearer prefix
const token = localStorage.getItem('token');
if (token) {
  const cleanToken = sanitizeToken(token);
  
  if (cleanToken) {
    api.defaults.headers.common['Authorization'] = `Bearer ${cleanToken}`;
    console.log('🔑 Token found and sanitized, applying to requests');
  } else {
    console.log('❌ Found invalid token in localStorage, removing it');
    localStorage.removeItem('token');
  }
} else {
  console.log('❌ No token found in localStorage');
}

// Add request interceptor for logging and token verification
api.interceptors.request.use(
  (config) => {
    // Verify token format if present in Authorization header
    const authHeader = config.headers.Authorization;
    if (authHeader && typeof authHeader === 'string') {
      // Make sure we're using a valid token
      const token = authHeader.replace(/^Bearer\s+/, '').trim();
      const cleanToken = sanitizeToken(token);
      
      if (cleanToken) {
        config.headers.Authorization = `Bearer ${cleanToken}`;
      } else {
        console.error('🚨 Invalid token detected in request, removing it');
        delete config.headers.Authorization;
        localStorage.removeItem('token');
      }
    }
    
    return config;
  },
  (error) => {
    console.error('❌ Request error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for auth errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('🚨 API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.message
    });
    
    // Only handle 401 errors for non-auth endpoints
    if (error.response?.status === 401 && 
        !error.config.url.includes('/login') && 
        !error.config.url.includes('/register')) {
      
      console.log('⚠️ Authentication error - clearing token');
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];
      
      // Don't redirect if already on login page
      if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
        // Use a slight delay to avoid potential loops during navigation
        setTimeout(() => {
          window.location.href = '/login?expired=true';
        }, 100);
      }
    }
    
    return Promise.reject(error);
  }
);

// Set up back-compatibility with code that uses axios directly
axios.defaults.baseURL = baseURL;
axios.defaults.headers.common = api.defaults.headers.common;

// Export configured axios instance
export default api;
